import { useState, useCallback, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { apiAdminGetAgentBetReport, apiAdminGetPartner, ApiRequestError } from '@/lib/api'
import type { MemberBetSummary, PaginatedResult } from '@nexbit/types'
import { formatCurrency } from '@/lib/utils'
import { format, startOfMonth, endOfDay } from 'date-fns'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, ArrowLeft, Search, TrendingUp, ChevronRight, ChevronLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useT } from '@/contexts/language'

function toDateInputValue(d: Date): string {
  return format(d, 'yyyy-MM-dd')
}

const inputClass = cn(
  'h-9 rounded-[var(--radius-sm)] border border-input bg-transparent px-3',
  'font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring',
)

export default function AdminReportsAgentPage() {
  const t = useT()
  const { partnerId } = useParams<{ partnerId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const initFrom = searchParams.get('from')
  const initTo = searchParams.get('to')
  const partnerCode = searchParams.get('partnerCode') ?? ''
  const agentShareRateParam = parseFloat(searchParams.get('agentShareRate') ?? '0')

  const [from, setFrom] = useState(
    initFrom ? format(new Date(initFrom), 'yyyy-MM-dd') : toDateInputValue(startOfMonth(new Date())),
  )
  const [to, setTo] = useState(
    initTo ? format(new Date(initTo), 'yyyy-MM-dd') : toDateInputValue(endOfDay(new Date())),
  )
  const [data, setData] = useState<PaginatedResult<MemberBetSummary> | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [agentShareRate, setAgentShareRate] = useState(agentShareRateParam)

  useEffect(() => {
    if ((agentShareRateParam > 0 && !isNaN(agentShareRateParam)) || !partnerId) return
    void apiAdminGetPartner(partnerId).then((p) => {
      setAgentShareRate(parseFloat(p.commissionRate))
    }).catch(() => {})
  // fetch once on mount to resolve share rate when not in URL
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearch = useCallback(async (e: React.FormEvent, p = 1) => {
    e.preventDefault()
    if (!partnerId) return
    setLoading(true)
    setError(null)
    setPage(p)
    try {
      const result = await apiAdminGetAgentBetReport(partnerId, {
        from: new Date(from).toISOString(),
        to: new Date(to + 'T23:59:59').toISOString(),
        page: p,
        limit: 20,
      })
      setData(result)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setLoading(false)
    }
  }, [partnerId, from, to, t])

  useEffect(() => {
    if (initFrom && initTo && partnerId) {
      void handleSearch({ preventDefault: () => {} } as React.FormEvent)
    }
  // run only on mount — date/partner come from URL and won't change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const activeItems = data?.items.filter((m) => m.betCount > 0) ?? []
  const totalPages = data ? Math.ceil(data.total / data.limit) : 0

  const totals = activeItems.reduce(
    (acc, m) => ({
      bets: acc.bets + m.betCount,
      bet: acc.bet + parseFloat(m.totalBetAmount),
      win: acc.win + parseFloat(m.totalWinAmount),
      net: acc.net + parseFloat(m.netResult),
    }),
    { bets: 0, bet: 0, win: 0, net: 0 },
  )

  function drilldown(m: MemberBetSummary) {
    const q = new URLSearchParams({
      partnerId: partnerId ?? '',
      from: new Date(from).toISOString(),
      to: new Date(to + 'T23:59:59').toISOString(),
      username: m.username,
      agentShareRate: String(agentShareRate),
    })
    navigate(`/admin/reports/agent/${partnerId ?? ''}/member/${m.memberId}?${q.toString()}`)
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl">
      <div className="flex items-center gap-3 mb-5">
        <Button variant="ghost" size="sm" className="h-7 gap-1.5 font-mono text-xs text-muted-foreground" asChild>
          <Link to="/admin/reports">
            <ArrowLeft className="h-3.5 w-3.5" />
            {t.reports.winLoss}
          </Link>
        </Button>
        <span className="text-muted-foreground/40">·</span>
        <span className="font-mono text-xs font-bold uppercase tracking-wider text-foreground">
          {partnerCode || t.reports.agent} — {t.betReport.title}
        </span>
      </div>

      <form onSubmit={(e) => void handleSearch(e)}>
        <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)] mb-4">
          <div className="px-4 py-2.5 bg-secondary">
            <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
              {t.common.filter}
            </span>
          </div>
          <div className="p-4 flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t.reports.from}</Label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} required className={inputClass} />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t.reports.to}</Label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} required className={inputClass} />
            </div>
            <Button type="submit" size="sm" className="h-9 font-mono text-xs gap-1.5" disabled={loading}>
              <Search className="h-3.5 w-3.5" />
              {t.common.search}
            </Button>
          </div>
        </div>
      </form>

      {error && (
        <div className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4" role="alert">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden">
          <div className="px-4 py-2.5 bg-secondary"><Skeleton className="h-3 w-24" /></div>
          <div className="divide-y divide-border">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex gap-4 px-4 py-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-20" />
              </div>
            ))}
          </div>
        </div>
      ) : data !== null ? (
        <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]">
          <div className="px-4 py-2.5 bg-secondary flex items-center gap-2">
            <TrendingUp className="h-3.5 w-3.5 text-secondary-foreground/60" />
            <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
              {t.common.total}: {activeItems.length}
            </span>
          </div>

          {activeItems.length === 0 ? (
            <div className="py-12 text-center">
              <p className="font-mono text-sm text-muted-foreground">{t.betReport.noActivity}</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t.betReport.member}</TableHead>
                    <TableHead className="text-right">{t.betReport.betCount}</TableHead>
                    <TableHead className="text-right">{t.betReport.betVolume}</TableHead>
                    <TableHead className="text-right">{t.betReport.payout}</TableHead>
                    <TableHead className="text-right">{t.betReport.net}</TableHead>
                    {agentShareRate > 0 && (
                      <>
                        <TableHead className="text-right">{t.reports.agentNet}</TableHead>
                        <TableHead className="text-right">{t.reports.adminNet}</TableHead>
                      </>
                    )}
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeItems.map((m) => (
                    <TableRow key={m.memberId} className="cursor-pointer hover:bg-muted/50" onClick={() => drilldown(m)}>
                      <TableCell>
                        <p className="font-mono text-sm font-semibold">{m.username}</p>
                        {m.displayName && <p className="font-mono text-xs text-muted-foreground">{m.displayName}</p>}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-muted-foreground">
                        {m.betCount.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCurrency(m.totalBetAmount)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCurrency(m.totalWinAmount)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {(() => {
                          const houseNet = -parseFloat(m.netResult)
                          return (
                            <span className={houseNet >= 0 ? 'text-success font-semibold' : 'text-destructive font-semibold'}>
                              {formatCurrency(houseNet.toFixed(2))}
                            </span>
                          )
                        })()}
                      </TableCell>
                      {agentShareRate > 0 && (() => {
                        const houseNet = -parseFloat(m.netResult)
                        const aNet = houseNet * agentShareRate
                        const dNet = houseNet * (1 - agentShareRate)
                        return (
                          <>
                            <TableCell className="text-right font-mono text-sm">
                              <span className={aNet >= 0 ? 'text-success font-semibold' : 'text-destructive font-semibold'}>
                                {formatCurrency(aNet.toFixed(2))}
                              </span>
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm">
                              <span className={dNet >= 0 ? 'text-success font-semibold' : 'text-destructive font-semibold'}>
                                {formatCurrency(dNet.toFixed(2))}
                              </span>
                            </TableCell>
                          </>
                        )
                      })()}
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 gap-1 font-mono text-xs"
                          onClick={(e) => { e.stopPropagation(); drilldown(m) }}
                        >
                          {t.betReport.viewBets}
                          <ChevronRight className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-secondary/30 font-semibold border-t-2 border-border">
                    <TableCell className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      {t.reports.totals}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {totals.bets.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatCurrency(totals.bet.toFixed(2))}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatCurrency(totals.win.toFixed(2))}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {(() => {
                        const hNet = -totals.net
                        return (
                          <span className={hNet >= 0 ? 'text-success' : 'text-destructive'}>
                            {formatCurrency(hNet.toFixed(2))}
                          </span>
                        )
                      })()}
                    </TableCell>
                    {agentShareRate > 0 && (() => {
                      const hNet = -totals.net
                      const tANet = hNet * agentShareRate
                      const tDNet = hNet * (1 - agentShareRate)
                      return (
                        <>
                          <TableCell className="text-right font-mono text-sm">
                            <span className={tANet >= 0 ? 'text-success' : 'text-destructive'}>
                              {formatCurrency(tANet.toFixed(2))}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            <span className={tDNet >= 0 ? 'text-success' : 'text-destructive'}>
                              {formatCurrency(tDNet.toFixed(2))}
                            </span>
                          </TableCell>
                        </>
                      )
                    })()}
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/30">
                  <span className="font-mono text-xs text-muted-foreground">{page} / {totalPages}</span>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1}
                      onClick={(e) => void handleSearch(e as unknown as React.FormEvent, page - 1)} aria-label={t.common.prev}>
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages}
                      onClick={(e) => void handleSearch(e as unknown as React.FormEvent, page + 1)} aria-label={t.common.next}>
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="border border-dashed border-border rounded-[var(--radius)] py-12 text-center">
          <p className="font-mono text-sm text-muted-foreground">{t.reports.noData}</p>
        </div>
      )}
    </div>
  )
}
